import type { BoardStepSection } from './boardModel';

/**
 * The rules the board's new affordances stand on (spec 098).
 *
 * All pure. The service accepts a move only along a transition the workflow
 * declares from the task's current step, so every offer the board makes — an
 * Undo, a "Move to…" list — is read from the same declarations the service
 * checks, and never from a guess.
 */

/** Whether `to` declares a transition back to `from` (FR-002). */
export function reverseTransitionExists(
  steps: readonly BoardStepSection[],
  from: string,
  to: string,
): boolean {
  const destination = steps.find((step) => step.stepId === to);
  return (destination?.transitions ?? []).some((t) => t.to === from);
}

/** The steps a column declares transitions to, in declaration order (FR-008). */
export function declaredTargets(
  steps: readonly BoardStepSection[],
  stepId: string,
): { stepId: string; label: string }[] {
  const origin = steps.find((step) => step.stepId === stepId);
  const byId = new Map(steps.map((step) => [step.stepId, step]));
  const seen = new Set<string>();
  const targets: { stepId: string; label: string }[] = [];
  for (const transition of origin?.transitions ?? []) {
    const target = byId.get(transition.to);
    if (!target || seen.has(target.stepId)) continue;
    seen.add(target.stepId);
    targets.push({ stepId: target.stepId, label: target.label });
  }
  return targets;
}

/**
 * Where `h`/`l` land (FR-019): the card in the neighbouring non-empty column
 * whose position is nearest the current one, ties toward the top; `null` at
 * the edge.
 */
export function nearestCardAcross(
  columns: readonly (readonly string[])[],
  col: number,
  index: number,
  dir: -1 | 1,
): { col: number; index: number } | null {
  for (let next = col + dir; next >= 0 && next < columns.length; next += dir) {
    const cards = columns[next];
    if (cards.length === 0) continue;
    return { col: next, index: Math.min(index, cards.length - 1) };
  }
  return null;
}

export type BulkOutcome =
  | { id: string; ok: true; changed: boolean }
  | { id: string; ok: false; reason: string };

/** Run one action per id, in order, never stopping at a refusal (FR-009). */
export async function runBulk(
  ids: readonly string[],
  act: (id: string) => Promise<BulkOutcome>,
  onProgress?: (done: number, total: number) => void,
): Promise<BulkOutcome[]> {
  const outcomes: BulkOutcome[] = [];
  for (const id of ids) {
    outcomes.push(await act(id));
    onProgress?.(outcomes.length, ids.length);
  }
  return outcomes;
}

/**
 * One sentence for the whole batch (FR-009): how many the verb applied to,
 * how many were refused and why. Reasons are named once each.
 */
export function summariseBulk(
  outcomes: readonly BulkOutcome[],
  verb: string,
): string {
  const done = outcomes.filter((o) => o.ok && o.changed).length;
  const unchanged = outcomes.filter((o) => o.ok && !o.changed).length;
  const refused = outcomes.filter((o) => !o.ok);
  if (refused.length === 0 && done === 0) return 'Nothing changed.';
  const parts: string[] = [];
  if (done > 0) parts.push(`${String(done)} ${verb}`);
  if (unchanged > 0) parts.push(`${String(unchanged)} already so`);
  if (refused.length > 0) {
    const reasons = [
      ...new Set(refused.map((o) => (o.ok ? '' : o.reason))),
    ].join(', ');
    parts.push(`${String(refused.length)} refused: ${reasons}`);
  }
  return `${parts.join(' · ')}.`;
}
